import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { servicesData } from "./ServicesData";
import { Phone, Mail, Send } from "lucide-react";
import { Helmet } from "react-helmet";
import Header from "../../layout/Header";
import Footer from "../../layout/Footer";

const ServicesDetail = () => {
  const { serviceId } = useParams();
  const service = servicesData.find((s) => s.id === serviceId);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    topic: service?.title || "",
    message: "",
  });

  const [success, setSuccess] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // Update topic when service changes
    setFormData((prev) => ({ ...prev, topic: service?.title || "" }));
  }, [service]);

  if (!service)
    return (
      <div className="py-40 text-center font-serif text-2xl">
        Service Not Found
      </div>
    );

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Only allow numbers for phone and max 10 digits
    if (name === "phone" && (value.length > 10 || /[^0-9]/.test(value))) return;

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (
      !formData.name ||
      !formData.email ||
      !formData.phone ||
      formData.phone.length !== 10
    ) {
      alert("Please fill all fields correctly!");
      return;
    }

    // WhatsApp message
    const waMessage = `Hello! I am interested in ${formData.topic}.\n\nName: ${formData.name}\nEmail: ${formData.email}\nPhone: ${formData.phone}\nMessage: ${formData.message}`;
    const phoneNumber = "919625975135"; // your WhatsApp number

    // Open WhatsApp
    window.open(
      `https://wa.me/${phoneNumber}?text=${encodeURIComponent(waMessage)}`,
      "_blank"
    );

    // Show success popup
    setSuccess(true);

    // Reset form
    setFormData({
      name: "",
      email: "",
      phone: "",
      topic: service.title,
      message: "",
    });

    // Hide popup after 3 seconds
    setTimeout(() => setSuccess(false), 3000);
  };

  return (
    <>
      {/* 🪄 Helmet Dynamic Setup */}
      <Helmet>
        <title>{service.helmetTitle}</title>
        <meta name="description" content={service.helmetDescription} />
        <meta name="keywords" content={service.helmetKeywords} />
        <noscript>{service.helmetnoscript}</noscript>
      </Helmet>

      <Header />
      <div className="bg-[#f4f7f6] font-serif">
        {/* 🏛️ HERO SECTION */}
        <div
          className="relative h-[500px] flex items-center justify-center bg-fixed bg-cover bg-center"
          style={{
            backgroundImage: `linear-gradient(rgba(11, 28, 45, 0.7),rgba(11, 28, 45, 0.7)), url(${service.bannerImage})`,
          }}
        >
          <div className="text-center z-10 px-4">
            <span className="text-[#d4a373] tracking-[0.4em] uppercase text-sm mb-4 block animate-fadeIn">
              Nyaysakti Law Firm
            </span>
            <h1 className="text-5xl md:text-7xl text-white font-bold uppercase tracking-tight mb-6 underline decoration-[#d4a373] decoration-4 underline-offset-8">
              {service.title}
            </h1>
            <div className="flex justify-center gap-2 text-gray-300 uppercase text-xs tracking-widest">
              <Link to="/" className="hover:text-[#d4a373]">
                Home
              </Link>{" "}
              <span>/</span> <span>{service.title}</span>
            </div>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 py-20">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            {/* 📝 LEFT SIDE: CONTENT & CARDS */}
            <div className="lg:col-span-8">
              <div className="mb-16">
                <h2 className="text-[#002b1f] text-4xl font-bold mb-6 italic">
                  Strategic Legal Solutions
                </h2>
                <p className="text-gray-600 text-lg leading-relaxed font-sans border-l-4 border-[#d4a373] pl-6 mb-8">
                  {service.fullDesc}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {service.featureDetails?.slice(0, 8).map((item, index) => (
                  <div
                    key={index}
                    className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-2xl transition-all duration-500 border border-gray-100"
                  >
                    <div className="relative h-60 overflow-hidden">
                      <img
                        src={item.img}
                        alt={item.name}
                        className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#002b1f]/80 to-transparent opacity-60"></div>
                      <div className="absolute bottom-4 left-4">
                        <h4 className="text-white text-xl font-bold uppercase tracking-wide">
                          {item.name}
                        </h4>
                      </div>
                    </div>
                    <div className="p-6">
                      <p className="text-gray-500 text-sm font-sans leading-relaxed mb-4">
                        {item.desc}
                      </p>
                      <div className="h-1 w-12 bg-[#0B1C2D] group-hover:w-full transition-all duration-500"></div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* 📩 RIGHT SIDE: STICKY CONTACT FORM */}
            <div className="lg:col-span-4">
              <div className="sticky top-28 space-y-8">
                <div className="bg-[#0B1C2D] rounded-2xl p-8 text-white shadow-2xl border-b-4 border-[#d4a373]">
                  <h3 className="text-2xl font-bold mb-2 text-[#d4a373] uppercase tracking-tighter">
                    Free Consultation
                  </h3>
                  <p className="text-gray-400 text-xs mb-8 font-sans">
                    Our experts will get back to you within 24 hours.
                  </p>

                  <form className="space-y-4 font-sans" onSubmit={handleSubmit}>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Full Name"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 p-3 rounded-lg focus:outline-none focus:border-[#d4a373] transition"
                      required
                    />
                    <input
                      type="email"
                      name="email"
                      placeholder="Email Address"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 p-3 rounded-lg focus:outline-none focus:border-[#d4a373] transition"
                      required
                    />
                    <input
                      type="tel"
                      name="phone"
                      placeholder="Phone Number"
                      value={formData.phone}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 p-3 rounded-lg focus:outline-none focus:border-[#d4a373] transition"
                      pattern="[0-9]{10}"
                      maxLength={10}
                      required
                      title="Please enter exactly 10 digits"
                    />
                    <select
                      name="topic"
                      value={formData.topic}
                      onChange={handleChange}
                      className="w-full bg-[#0B1C2D] border border-white/10 p-3 rounded-lg focus:outline-none focus:border-[#d4a373] text-gray-400"
                    >
                      <option>{service.title}</option>
                      <option>Other Legal Matters</option>
                    </select>
                    <textarea
                      name="message"
                      rows="4"
                      placeholder="How can we help you?"
                      value={formData.message}
                      onChange={handleChange}
                      className="w-full bg-white/5 border border-white/10 p-3 rounded-lg focus:outline-none focus:border-[#d4a373] transition"
                    />
                    <button
                      type="submit"
                      className="w-full bg-[#d4a373] hover:bg-white text-[#002b1f] font-bold py-4 rounded-lg flex items-center justify-center gap-2 transition-all group"
                    >
                      SEND MESSAGE{" "}
                      <Send
                        size={18}
                        className="group-hover:translate-x-1 transition-transform"
                      />
                    </button>
                  </form>
                </div>

                {/* Success popup */}
                {success && (
                  <div className="fixed bottom-8 right-8 bg-green-500 text-white px-6 py-4 rounded-lg shadow-lg animate-fadeIn">
                    Message sent successfully! ✅
                  </div>
                )}

                {/* 📞 QUICK CONTACT INFO */}
                <div className="bg-white p-8 rounded-2xl shadow-lg border border-gray-100">
                  <h4 className="text-[#002b1f] font-bold mb-6 uppercase text-xs tracking-widest border-b pb-2 border-[#d4a373]">
                    Direct Contact
                  </h4>
                  <div className="space-y-4 text-sm font-sans">
                    <div className="flex items-center gap-3 text-gray-600 hover:text-[#d4a373] transition pointer cursor-pointer">
                      <Phone size={16} className="text-[#d4a373]" />{" "}
                      <span>+91 96259 75135</span>
                    </div>
                    <div className="flex items-center gap-3 text-gray-600 hover:text-[#d4a373] transition pointer cursor-pointer">
                      <Mail size={16} className="text-[#d4a373]" />{" "}
                      <span className="text-xs">arunchaudhri1258@gmail.com</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ServicesDetail;